const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

/** Great-circle distance in kilometers. */
export const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/** Initial bearing in degrees (0 = north, 90 = east) from point A to point B. */
export const bearingDegrees = (lat1, lng1, lat2, lng2) => {
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δλ = toRad(lng2 - lng1);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x =
        Math.cos(φ1) * Math.sin(φ2) -
        Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

/**
 * Straight-line pickup -> destination distance in kilometers, mirroring
 * `CalculationHelper.estimatedDistanceFromLocationsInKilometers` in the iOS and Android
 * apps (Pythagorean approximation, degrees converted to km with a cos-latitude correction
 * on the longitude side).
 *
 * This is the number that goes into `drives.estimated_distance` — the driver app rewrites
 * the same estimate when it accepts a drive, so the web has to compute it the same way or
 * one ride reports two distances depending on who ordered it. An estimate by nature: the
 * distance actually driven is only known once the recorded trajectory is measured, and that
 * is what a finished ride is billed on.
 *
 * Distinct from `haversineKm` above, which stays the general-purpose geometry used for
 * driver heading and the dispatcher's arrival estimate — those are web-only numbers with
 * no mobile counterpart to stay in step with.
 */
export const estimatedDistanceFromLocationsInKilometers = (lat1, lng1, lat2, lng2) => {
    const latDiff = (lat2 - lat1) * 111;
    const lngDiff = (lng2 - lng1) * 111 * Math.cos((lat1 * Math.PI) / 180);
    return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
};
